<template>
  <SignupMainContainer
    :title="'Log in to Bottle'"
    :image-position="'right'"
    :max-width="'md:max-w-xl'"
    hide-log-out
    @form-submitted="loginAction"
  >
    <template #body>
      <div>
        <label
          :for="legacy ? 'handle' : 'email'"
          class="block text-gray-700 text-sm font-medium leading-5 tracking-tighter"
        >
          {{ legacy ? 'Your username' : 'Your email address' }}
        </label>
        <div v-if="legacy" class="mt-1 flex rounded-md shadow-sm">
          <input
            id="handle"
            v-model="handle"
            type="text"
            name="handle"
            autocomplete="username"
            required
            tabindex="1"
            class="peer placeholder-gray-400 block px-3 py-3.5 rounded-l-lg w-full focus:border-gray-800 border-2 border-gray-200 border-r-0 focus:ring-0 appearance-none transition duration-150 ease-in-out text-sm leading-5 focus:outline-none font-semibold tracking-wide"
            placeholder="BananaStand"
          />
          <span
            class="w-full inline-flex items-center rounded-r-lg border-2 border-l-0 peer-focus:border-gray-800 border-gray-200 bg-gray-100 px-3 text-gray-600 text-sm tracking-tight"
          >
            .bottle.com
          </span>
        </div>
        <div v-else class="mt-1 rounded-md shadow-sm">
          <input
            id="email"
            v-model="email"
            type="email"
            name="email"
            autocomplete="email"
            required
            tabindex="1"
            class="placeholder-gray-400 block px-3 py-3.5 rounded-lg w-full focus:border-gray-800 border-2 border-gray-200 focus:ring-0 appearance-none transition duration-150 ease-in-out text-sm leading-5 focus:outline-none font-semibold tracking-wide"
            placeholder="your@email.com"
          />
        </div>
      </div>

      <div>
        <div class="flex justify-between text-gray-700 text-sm font-medium leading-5 tracking-tighter">
          <label for="password">Password</label>
          <router-link :to="legacy ? '/forgot-password' : '/forgot-password-email'" class="underline">
            Forgot your password?
          </router-link>
        </div>
        <div class="mt-1 rounded-md shadow-sm">
          <PasswordInput
            id="password"
            v-model="password"
            autocomplete="current-password"
            placeholder="Password"
            required
            :class="[error ? 'border-red-400' : 'border-gray-300', flash && 'bg-red-400']"
            tabindex="1"
          />
        </div>
      </div>
      <div v-if="error" class="bg-yellow-200 text-sm text-gray-800 mt-2 py-1 px-2 rounded-md tracking-tighter">
        {{ error }}
        <div v-if="tries >= 3">Contact team@bottle.com for help</div>
      </div>
      <ConvertedToEmailNotice v-if="convertedError" @action="$router.push('/login')" />
    </template>
    <template #footer>
      <div class="w-full flex flex-col">
        <div class="w-full flex justify-between">
          <button
            type="button"
            class="inline-flex items-center justify-center border-gray-800 border-2 text-md hover:shadow-lg transition-all rounded-lg px-6 py-3.5 leading-tight bg-white text-gray-800 tracking-tighter"
            @click="signUpClicked()"
          >
            <span>
              New to Bottle?
              <br class="block sm:hidden" />
              <span class="font-bold">Schedule a Demo</span>
            </span>
          </button>
          <button
            type="submit"
            class="inline-flex shrink-0 items-center border-gray-800 border-2 font-bold text-md hover:shadow-lg transition-all rounded-full px-6 py-3.5 leading-tight bg-yellow-200 text-gray-800"
            tabindex="1"
          >
            <span class="ml-auto transform -translate-y-px mr-4">Log in</span>
            <span class="mr-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="13" viewBox="0 0 34 13">
                <path
                  d="M3.11,6.82A20,20,0,0,1,1.2,6a.46.46,0,0,0-.67.15A2.32,2.32,0,0,0,0,7.25C0,7.64,0,7.84.3,8c.59.25,1.18.52,1.78.72"
                  transform="translate(0 0)"
                ></path>
                <path
                  d="M1.83,8.58A23.56,23.56,0,0,0,7.36,10a36.41,36.41,0,0,0,8.79,0A53.55,53.55,0,0,0,29.69,6.72l.19-.07,0,0c-.33.4-.65.8-1,1.19-.72.87-1.45,1.73-2.16,2.61a1.75,1.75,0,0,0,0,2.28.68.68,0,0,0,.52.22.73.73,0,0,0,.5-.26c.67-.78,1.33-1.58,2-2.39l3.65-4.41A2.13,2.13,0,0,0,34,4.5,1.24,1.24,0,0,0,33.91,4a.87.87,0,0,0-.28-.36,1.55,1.55,0,0,0-.32-.2L26.59.11c-.65-.31-1.25.05-1.54.92a1.22,1.22,0,0,0,.4,1.5c.95.49,1.91,1,2.86,1.42l1.78.89-.19.08A55.6,55.6,0,0,1,17.44,8a36.08,36.08,0,0,1-8.87.12A21.78,21.78,0,0,1,2.82,6.7c-.3-.12-.73-.1-1-.25"
                  fill="#000000"
                  transform="translate(0 0)"
                ></path>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </template>
  </SignupMainContainer>
  <NotificationSuccess :show="passwordReset" :message="successMessage" />
</template>
<script>
import ConvertedToEmailNotice from '@/components/ConvertedToEmailNotice.vue'
import Notification from '@/components/NotificationSuccess.vue'
import { clearAuthToken } from '@/helpers/authToken'
import { logInWithToken } from '@/helpers/tokenLogin'
import api from '@/store/api'

export default {
  components: { Notification, ConvertedToEmailNotice },
  props: {
    legacy: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      email: '',
      handle: '',
      password: '',
      error: '',
      convertedError: false,
      tries: 0,
      flash: false,
      passwordReset: false,
      successMessage: ''
    }
  },
  watch: {
    legacy() {
      this.error = ''
      this.convertedError = false
    }
  },
  mounted() {
    clearAuthToken()
    const resetLinkSent = this.$route.fullPath.includes('reset-link-sent')
    const passwordWasSet = this.$route.fullPath.includes('reset-success')
    this.passwordReset = resetLinkSent || passwordWasSet
    if (this.passwordReset) {
      this.successMessage = resetLinkSent
        ? "If an account exists for that email, we've sent a password reset link. Please check your inbox."
        : 'Your password has been set. Please login to access your dashboard.'
    }
  },
  methods: {
    loginAction: function () {
      const apiEndpoint = `tokens`
      const body = this.legacy
        ? {
            handle: this.handle,
            password: this.password
          }
        : {
            authentication_type: 'system_user',
            email: this.email.replace(/\s/g, '').toLowerCase(),
            password: this.password
          }

      api
        .call('POST', apiEndpoint, body, false)
        .then(response => {
          this.authenticate(response.data.token)
        })
        .catch(error => {
          if (error?.response?.data?.converted_to_email_signin) {
            this.convertedError = true
            this.error = ''
            return
          }
          this.error = this.legacy ? 'Incorrect handle or password' : 'Incorrect email or password'
          this.tries++
          if (this.tries > 1) {
            this.flash = true
            setTimeout(() => {
              this.flash = false
            }, 200)
          }
        })
    },
    signUpClicked() {
      window.location = 'https://www.bottle.com/book-a-demo'
    },
    authenticate: async function (token) {
      const loggedIn = await logInWithToken(token, { store: this.$store, router: this.$router })
      if (!loggedIn) this.error = 'Your account does not have access to any resources'
    }
  }
}
</script>
